import { array, date, mixed, number, object, string, boolean } from 'yup';

import { RiskAction } from '@assess/shared/ontology-types';

import { ActionValue } from '../enum/action-value.enum';
import { FormRiskEntrySourceType } from '../enum/form-risk-entry-source-type.enum';
import { codeValidator } from '../functions/code-validator.function';

export const defaultFormRiskEntryValidationConfig = {
  validateAction: true,
  validateAlternateCode: true,
  validateAttachments: true,
  validateDateOfService: true,
  validateClaim: true,
  validateNotes: true,
  validatePlan: true,
  validateProvider: true,
  validateReasonCode: true,
};

export interface IFormRiskEntryValidationConfig {
  validateAction?: boolean;
  validateAlternateCode?: boolean;
  validateAttachments?: boolean;
  validateDateOfService?: boolean;
  validateClaim?: boolean;
  validateNotes?: boolean;
  validatePlan?: boolean;
  validateProvide?: boolean;
  validateReasonCode?: boolean;
}

export enum FormRiskEntrySchemaKey {
  ACTION = 'data.action',
  ALTERNATE_CODE = 'data.alternateCode',
  ATTACHMENTS = 'attachments',
  CLAIM = 'data.claim',
  DATE_OF_SERVICE = 'data.dateOfService',
  NOTES = 'data.notes',
  PLAN = 'data.plan',
  PROVIDER = 'data.provider',
  REASON_CODE_ID = 'reasonCodeId',
}

export enum FormRiskEntryErrorType {
  NULLABLE = 'nullable',
  REQUIRED = 'required',
}

export const getCurrentRiskAction = (config: any, actionValue: string) => {
  if (config && config.riskActions && actionValue) {
    const riskAction = config?.riskActions.find((action: any) => action.type === actionValue);
    if (riskAction) {
      return riskAction;
    }

    return null;
  }
};

export const alternateCodeSchema = object().when(['$config', 'action'], ([config, action], schema) => {
  const isEvidenceValidationRequired =
    config?.validateAlternateCode &&
    config?.sourceType !== FormRiskEntrySourceType.User &&
    getCurrentRiskAction(config, action)?.alternateCode === ActionValue.Required;

  const isEvidenceValid = codeValidator(config);

  return !isEvidenceValidationRequired || isEvidenceValid
    ? schema.nullable().notRequired()
    : schema.required('Alternate Code is a required field');
});

export const claimSchema = object({
  code: string(),
}).when(['$config', 'action'], ([config, action], schema) => {
  return config?.validateClaim &&
    config?.linkToClaimEnabled &&
    getCurrentRiskAction(config, action)?.linkToClaim === ActionValue.Required
    ? schema.required('A claim must be linked')
    : schema.nullable().notRequired();
});

export const notesSchema = string()
  .max(500, 'Notes must be 500 characters or less')
  .when(['$config', 'action'], ([config, action], schema) => {
    return config?.validateNotes &&
      config?.notesEnabled &&
      getCurrentRiskAction(config, action)?.notes === ActionValue.Required
      ? schema.trim().required('Notes are required')
      : schema.trim().nullable().notRequired();
  });

export const actionSchema = mixed().when(['$config'], ([config], schema) => {
  return config?.validateAction ? schema.oneOf(Object.values(RiskAction)).required() : schema.nullable().notRequired();
});

export const providerSchema = object().when(['$config', 'action'], ([config, action], schema) => {
  return config?.validateProvider &&
    config?.providerEnabled &&
    getCurrentRiskAction(config, action)?.provider === ActionValue.Required
    ? schema.required('Provider is required')
    : schema.nullable().notRequired();
});

export const dateOfServiceSchema = date().when(['$config', 'action'], ([config, action], schema) => {
  const { dateOfServiceMin, dateOfServiceMax } = config as { dateOfServiceMin: Date; dateOfServiceMax: Date };
  let minMax = schema;

  if (config?.validateDateOfService && config?.dosEnabled) {
    if (dateOfServiceMin && dateOfServiceMax) {
      minMax = schema
        .min(dateOfServiceMin, `Date of Service must be later than ${dateOfServiceMin.toLocaleDateString()}`)
        .max(dateOfServiceMax, `Date of Service must be earlier than ${dateOfServiceMax.toLocaleDateString()}`);
    } else if (dateOfServiceMin) {
      minMax = schema.min(
        dateOfServiceMin,
        `Date of Service must be later than ${dateOfServiceMin.toLocaleDateString()}`
      );
    } else if (dateOfServiceMax) {
      minMax = schema.max(
        dateOfServiceMax,
        `Date of Service must be earlier than ${dateOfServiceMax.toLocaleDateString()}`
      );
    }
  }

  return config?.validateDateOfService &&
    config?.dosEnabled &&
    getCurrentRiskAction(config, action)?.dateOfService === ActionValue.Required
    ? minMax.required('Date of Service is required').typeError('Date of Service validation failed')
    : minMax.nullable().notRequired();
});

export const planGroupSchema = array()
  .of(
    object({
      type: string().required(),
      checked: boolean(),
      text: string().when(['checked'], {
        is: true,
        then: (schema) => schema.required('Plan is required').max(500, 'Plan must be 500 characters or less'),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    })
  )
  .when(['$config', 'action'], ([config, action], schema) => {
    return config?.validatePlan &&
      config?.planEnabled &&
      getCurrentRiskAction(config, action)?.plan === ActionValue.Required
      ? schema.required('Plan is required').test({
          message: 'Plan is required',
          test: (plans) => {
            let isPlanSectionValid = false;

            const isPlanFieldsValid = plans?.every((v) => {
              isPlanSectionValid = (v as any).checked || isPlanSectionValid;

              return !(v as any).checked || ((v as any).checked && (v as any).text?.length);
            });

            return isPlanSectionValid && isPlanFieldsValid;
          },
        })
      : schema.nullable().notRequired();
  });

export const dataSchema = object({
  notes: notesSchema,
  action: actionSchema,
  provider: providerSchema,
  alternateCode: alternateCodeSchema,
  claim: claimSchema,
  plan: planGroupSchema,
  dateOfService: dateOfServiceSchema,
});

export const reasonCodeSchema = string().when(['$config', 'data'], ([config, data], schema) => {
  return config?.validateReasonCode &&
    config?.reasonCodesEnabled &&
    getCurrentRiskAction(config, data?.action)?.reasonCode === ActionValue.Required
    ? schema.trim().uuid().required('Reason Code is a required field')
    : schema.nullable().notRequired();
});

export const attachmentsSchema = array()
  .of(
    object({
      data: object({
        fileName: string().required(),
      }).required(),
      entityClass: string().required(),
      entityId: string().required(),
      entityVersion: number().required(),
    })
  )
  .when(['$config', 'data'], ([config, data], schema) => {
    return config?.validateAttachments &&
      config?.attachmentEnabled &&
      getCurrentRiskAction(config, data.action)?.attachment === ActionValue.Required
      ? schema.required('Attachments are required').min(1, 'Attachments are required')
      : schema.nullable().notRequired();
  });

export const formRiskEntrySchema = object({
  reasonCodeId: reasonCodeSchema,
  attachments: attachmentsSchema,
  data: dataSchema,
});
